package com.evoapps.evoshift.ui.adaptive

import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlin.math.max
import kotlin.math.min

/*
 * SH-102: foldables. iPhone Duo (docs/platforms/iphone-duo.md), Android foldables in tabletop or
 * book posture and the Googlebook/large-screen work (SH-98) share one small model: the window is
 * either an ordinary one, a foldable opened flat, or a foldable half-opened with a hinge across
 * it. Screens never read the platform APIs; they read [LocalDevicePosture] (through
 * [PostureAwareScaffold]) and decide with the pure functions below, which the unit tests cover.
 */

/** Which way the hinge runs across the window. */
enum class FoldOrientation {
    /** The hinge runs left to right: a top half and a bottom half (tabletop / laptop). */
    HORIZONTAL,
    /** The hinge runs top to bottom: a left half and a right half (book). */
    VERTICAL
}

/** The device's posture as far as the app's window is concerned. */
@Immutable
sealed interface DevicePosture {
    /** A phone, tablet, desktop or browser window, or a foldable folded shut (the outer display). */
    data object Normal : DevicePosture

    /** A foldable's inner display opened flat: a nearly square screen without a usable fold. */
    data object Flat : DevicePosture

    /**
     * A foldable partly folded. [hingeBounds] is the fold (or the hinge's reserved region) in
     * window coordinates, pixels; zero-width on a seamless fold.
     */
    data class HalfOpened(val hingeBounds: Rect, val orientation: FoldOrientation) : DevicePosture
}

/**
 * The posture the app shell provides (App.kt). Tests and the store screenshots override it with
 * [LocalDevicePostureOverride]; screens read this one.
 */
val LocalDevicePosture = compositionLocalOf<DevicePosture> { DevicePosture.Normal }

/** A fixed posture instead of the device's (UI tests, store screenshots); null = the device's. */
val LocalDevicePostureOverride = staticCompositionLocalOf<DevicePosture?> { null }

/**
 * The device's posture from the platform: Jetpack WindowManager's FoldingFeature on Android,
 * the iPhone Duo hinge on iOS (bridged from Swift), always [DevicePosture.Normal] on desktop and
 * the web.
 */
@Composable
expect fun rememberPlatformDevicePosture(): DevicePosture

/** How a posture-aware screen arranges its content and its controls. */
enum class PostureLayout {
    /** The screen's ordinary layout (phones, tablets, desktop: unchanged). */
    SINGLE,
    /** Content above the hinge, controls below it (laptop / tabletop posture). */
    TABLETOP,
    /** Content left of the hinge, controls right of it (book posture). */
    BOOK,
    /** A flat, nearly square foldable display: content and controls side by side, 58 / 42. */
    TWO_PANE
}

/** Widest-to-narrowest side ratio still "nearly square": iPhone Duo's inner display is 1.42. */
const val NearlySquareMaxRatio = 1.6f

/** The narrowest side a two-pane layout needs; a folded phone-sized screen keeps one pane. */
val TwoPaneMinSide: Dp = 560.dp

/** Whether a [width] x [height] window is nearly square (ratio up to [NearlySquareMaxRatio]). */
fun isNearlySquare(width: Dp, height: Dp): Boolean {
    val short = min(width.value, height.value)
    if (short <= 0f) return false
    return max(width.value, height.value) / short <= NearlySquareMaxRatio
}

/** The layout for [posture] in a [width] x [height] area. */
fun postureLayoutFor(posture: DevicePosture, width: Dp, height: Dp): PostureLayout = when (posture) {
    DevicePosture.Normal -> PostureLayout.SINGLE
    DevicePosture.Flat ->
        if (isNearlySquare(width, height) && min(width.value, height.value) >= TwoPaneMinSide.value) PostureLayout.TWO_PANE
        else PostureLayout.SINGLE
    is DevicePosture.HalfOpened -> when (posture.orientation) {
        FoldOrientation.HORIZONTAL -> PostureLayout.TABLETOP
        FoldOrientation.VERTICAL -> PostureLayout.BOOK
    }
}

/**
 * Where a hinge cuts an area: [before] px above (or left of) it, [hinge] px of the hinge itself
 * and [after] px below (or right of) it.
 */
data class HingeSplit(val before: Float, val hinge: Float, val after: Float)

/**
 * How [hinge] (window px) splits [area] (window px, the composable's bounds) along [orientation];
 * null when the hinge does not cross the area or leaves one side under [minSide] px (then the
 * screen keeps its ordinary layout rather than squeezing controls into a sliver).
 */
fun hingeSplit(area: Rect, hinge: Rect, orientation: FoldOrientation, minSide: Float): HingeSplit? {
    val (start, end, hingeStart, hingeEnd) = when (orientation) {
        FoldOrientation.HORIZONTAL -> listOf(area.top, area.bottom, hinge.top, hinge.bottom)
        FoldOrientation.VERTICAL -> listOf(area.left, area.right, hinge.left, hinge.right)
    }
    // The hinge has to run across the area, not only touch it.
    val crosses = when (orientation) {
        FoldOrientation.HORIZONTAL -> hinge.left < area.right && hinge.right > area.left
        FoldOrientation.VERTICAL -> hinge.top < area.bottom && hinge.bottom > area.top
    }
    if (!crosses) return null
    val before = hingeStart - start
    val after = end - hingeEnd
    if (before < minSide || after < minSide) return null
    return HingeSplit(before = before, hinge = (hingeEnd - hingeStart).coerceAtLeast(0f), after = after)
}

/**
 * iOS before the hinge API (built with Xcode 27.0, or iOS < 27.1): an iPhone window that is
 * nearly square and at least [TwoPaneMinSide] on its short side can only be iPhone Duo's inner
 * display opened flat; no earlier iPhone has such a window (the largest is 440 x 956 pt).
 */
fun phoneWindowIsUnfolded(isPhone: Boolean, width: Dp, height: Dp): Boolean =
    isPhone && isNearlySquare(width, height) && min(width.value, height.value) >= TwoPaneMinSide.value

/**
 * The posture a platform reports, as [DevicePosture]: [state] is the fold's state (null: no fold
 * in this window), [hinge] the fold's bounds in window px (null: not known). Half-opened without
 * bounds lays out as flat: the app cannot keep controls off a hinge it cannot place.
 */
fun devicePostureOf(state: FoldState?, hinge: Rect?): DevicePosture = when {
    state == FoldState.HALF_OPENED && hinge != null -> DevicePosture.HalfOpened(
        hinge,
        // A hinge wider than tall runs left to right.
        if (hinge.width >= hinge.height) FoldOrientation.HORIZONTAL else FoldOrientation.VERTICAL
    )
    state == FoldState.FLAT || state == FoldState.HALF_OPENED -> DevicePosture.Flat
    else -> DevicePosture.Normal
}

/** A fold's state as the platforms report it (WindowManager's FoldingFeature.State, UIHinge.Status). */
enum class FoldState { FLAT, HALF_OPENED, CLOSED }

/** Half-opened with a horizontal hinge: held like a laptop or stood on a table. */
val DevicePosture.isTabletop: Boolean
    get() = this is DevicePosture.HalfOpened && orientation == FoldOrientation.HORIZONTAL
